/**
 * Network error types and their user-friendly messages
 */
export enum NetworkErrorType {
  OFFLINE = 'OFFLINE',
  TIMEOUT = 'TIMEOUT',
  SERVER_ERROR = 'SERVER_ERROR',
  NETWORK_ERROR = 'NETWORK_ERROR',
  ABORT = 'ABORT',
  UNKNOWN = 'UNKNOWN',
}

export interface NetworkError extends Error {
  type: NetworkErrorType;
  userMessage: string;
  isRetryable: boolean;
  originalError?: Error;
}

/**
 * User-friendly error messages in Slovak
 */
const ERROR_MESSAGES = {
  [NetworkErrorType.OFFLINE]:
    'Nie ste pripojený na internet. Skontrolujte pripojenie a skúste znova.',
  [NetworkErrorType.TIMEOUT]: 'Požiadavka trvala príliš dlho. Skúste znova.',
  [NetworkErrorType.SERVER_ERROR]: 'Problém so serverom. Skúste znova neskôr.',
  [NetworkErrorType.NETWORK_ERROR]:
    'Problém s pripojením. Skontrolujte internetové pripojenie.',
  [NetworkErrorType.ABORT]: 'Požiadavka bola zrušená.',
  [NetworkErrorType.UNKNOWN]: 'Nastala neočakávaná chyba. Skúste znova.',
};

/**
 * Which error types can be retried
 */
const RETRYABLE_ERRORS = new Set([
  NetworkErrorType.TIMEOUT,
  NetworkErrorType.NETWORK_ERROR,
  NetworkErrorType.SERVER_ERROR,
]);

/**
 * Creates a NetworkError from a native error
 */
export function createNetworkError(error: Error): NetworkError {
  let type: NetworkErrorType;

  // Check if it's an AbortError
  if (error.name === 'AbortError') {
    type = NetworkErrorType.ABORT;
  }
  // Check if it's a fetch failure (usually network issues)
  else if (error.message === 'Failed to fetch') {
    // If we're offline, it's an offline error
    if (!navigator.onLine) {
      type = NetworkErrorType.OFFLINE;
    } else {
      // Otherwise it's a general network error
      type = NetworkErrorType.NETWORK_ERROR;
    }
  }
  // Check for timeout errors
  else if (error.message.includes('timeout') || error.name === 'TimeoutError') {
    type = NetworkErrorType.TIMEOUT;
  }
  // Check for server errors (if we have status codes)
  else if (
    error.message.includes('500') ||
    error.message.includes('502') ||
    error.message.includes('503')
  ) {
    type = NetworkErrorType.SERVER_ERROR;
  }
  // Default to unknown
  else {
    type = NetworkErrorType.UNKNOWN;
  }

  const networkError = new Error(error.message) as NetworkError;
  networkError.name = 'NetworkError';
  networkError.type = type;
  networkError.userMessage = ERROR_MESSAGES[type];
  networkError.isRetryable = RETRYABLE_ERRORS.has(type);
  networkError.originalError = error;
  networkError.stack = error.stack;

  return networkError;
}

/**
 * Enhanced API wrapper with better error handling
 */
export function networkErrorApiWrapper<T extends object>(apiInstance: T): T {
  return new Proxy(apiInstance, {
    get(target, propKey) {
      const origProp = target[propKey as keyof T];

      // If it's not a function, return as is
      if (typeof origProp !== 'function') {
        return origProp;
      }

      // Wrap all API methods with error handling
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return async function (this: any, ...args: any[]) {
        try {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          return await (origProp as any).apply(target, args);
        } catch (error) {
          // Convert to NetworkError for better handling
          if (error instanceof Error) {
            throw createNetworkError(error);
          }
          throw error;
        }
      };
    },
  });
}

/**
 * Hook for handling network errors in React components
 */
export function getErrorDisplayInfo(error: unknown): {
  title: string;
  message: string;
  isRetryable: boolean;
} {
  if (error && typeof error === 'object' && 'type' in error) {
    const networkError = error as NetworkError;
    return {
      title: 'Chyba pripojenia',
      message: networkError.userMessage,
      isRetryable: networkError.isRetryable,
    };
  }

  // Fallback for non-network errors
  if (error instanceof Error) {
    return {
      title: 'Chyba',
      message: error.message || 'Nastala neočakávaná chyba.',
      isRetryable: false,
    };
  }

  return {
    title: 'Chyba',
    message: 'Nastala neočakávaná chyba.',
    isRetryable: false,
  };
}
